import { TIMESERIES_TOP_ORDER } from "@const/timeseriesRequest.contants";
import { SettingKey, SettingsItem } from "@dto/settings.dto";
import { ThemeName, UpdateLoggedUserDTO, UserResponseDTO } from "@dto/usersResponse.dto";
import { Button, Checkbox, Col, Descriptions, Form, Input, InputNumber, Row, Switch, Tag, Tooltip } from "antd";
import { AxiosError } from "axios";
import { debounce } from "lodash-es";
import { FC, useEffect, useMemo, useState } from "react";
import { useAppGlobStore } from "src/appGlobalStore";
import { PasswordUtil } from "src/common/util/PasswordUtil";
import { SEPARATORS_CSV, SEPARATORS_DECIMAL } from "src/content/configuration/application/GlobalOptions";
import { API_URL } from "src/data/Api";
import { Label } from "src/data/Label";
import { BasicFormProps } from "src/data/props";
import { Log } from "src/service/log.service";
import { getSettings } from "src/service/settings.service";
import { useTheme } from "src/theme/ThemeProvider";
import { GLOB } from "src/util/Glob";
import { PasswordUtil as PasswordUtilUI } from "src/util/PasswordUtil";
import { ThemeUtil } from "src/util/ThemeUtil";
import { putApi } from "src/util/apiCalls";
import { nameof } from "ts-simple-nameof";
import { XmInputPassword } from "../../component/inputPassword/XmInputPassword";
import { XmModal } from "../../component/modal/XmModal";
import { XmSelect } from "../../component/select/XmSelect";
import XmTimeZoneSelect from "../../component/timezoneSelect/XmTimeZoneSelect";
import './UserProfile.less';

const graphItemsText = 'graph top items';
const searchHistoryText = 'search history';
const toolTipHistoryText = 'Number of records stored in Search history';
const toolTipHistoryDisableText = 'Enable/Disable Search history';
const toolTipItemsText = 'Show only the top N items in graphs. Group the rest into a single item called "Others".';
const toolTipSeparatorDisableText = 'Enable/Disable custom decimal/CSV separator.';

const { Item } = Descriptions;

function path(selector: (obj: UpdateLoggedUserDTO) => unknown) {
  return nameof(selector);
}

export function updateUser(userUpdate: UpdateLoggedUserDTO) {
  return putApi<UserResponseDTO, UpdateLoggedUserDTO>(API_URL.USER, userUpdate);
}

function updateUserInfo(userUpdate: UpdateLoggedUserDTO, updateText: string) {
  return updateUser(userUpdate).then(
    response => {
      GLOB.userInfo = { ...GLOB.userInfo, ...response.data.data };
      Log.success(`User ${updateText} set.`);
    }, (reason: AxiosError) => {
      Log.error(`Failed to set user's ${updateText}`, reason);
    });
}

const UserProfile: FC = () => {
  const [passwordRules, setPasswordRules] = useState<Record<string, string | number | boolean>>({});
  const [passModalOpen, setPassModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [topItems, setTopItems] = useState(GLOB?.userInfo?.configuration?.top);
  const [historyItems, setHistoryItems] = useState(GLOB?.userInfo?.configuration?.searchHistoryLimit);
  const [showReleaseNotes, setShowReleaseNotes] = useState(GLOB?.userInfo?.configuration?.showReleaseNotes);
  const [decimalSeparator, setDecimalSeparator] = useState(GLOB?.userInfo?.configuration?.decimalSeparator);
  const [csvSeparator, setCsvSeparator] = useState(GLOB?.userInfo?.configuration?.csvSeparator);
  const [globSettings, setGlobSettings] = useState<SettingsItem>({});
  const { theme } = useTheme();
  const { sso_server } = GLOB.userInfo;

  const [passwordForm] = Form.useForm();
  const [emailForm] = Form.useForm();

  useEffect(() => {
    void getSettings(SettingKey.PASSWORD.PREFIX).then(response => {
      const defaults = PasswordUtil.getDefaults();
      const settings = response.data.data;
      SettingKey.PASSWORD.reType(settings);
      for (const setting in defaults) {
        settings[setting] = settings[setting] ?? defaults[setting];
      }
      setPasswordRules(settings);
    });
    void getSettings(SettingKey.GLOB.PREFIX).then(response => {
      const settings = response.data.data;
      SettingKey.GLOB.reType(settings);
      setGlobSettings(settings);
    });
  }, []);

  const changeTopItemsDebounced = useMemo(() => debounce((top: number, searchHistoryLimit: number, itemText: string) => {
    void updateUserInfo({ configuration: { top, searchHistoryLimit } }, itemText);
  }, 1000), []);

  const setTheme = (name: ThemeName) => {
    ThemeUtil.setTheme(name);
    !useAppGlobStore.getState().info.backend.demo && void updateUserInfo({ configuration: { theme: name } }, `${name} theme`);
  };

  const changeShowReleaseNotes = (show: boolean) => {
    void updateUserInfo({ configuration: { showReleaseNotes: show } }, 'show release info').then(() => {
      setShowReleaseNotes(show);
    });
  };

  const closePasswordModal = () => {
    setPassModalOpen(false);
    passwordForm.resetFields();
  };

  const handlePasswordUpdate = (values: UpdateLoggedUserDTO) => {
    setConfirmLoading(true);
    updateUser({ oldPassword: values.oldPassword, password: values.password })
      .then(() => {
        closePasswordModal();
        Log.success('User password changed');
      }, (reason: AxiosError) => Log.error('Failed to change user password!', reason))
      .finally(() => setConfirmLoading(false));
  };

  const closeEmailModal = () => {
    setEmailModalOpen(false);
    emailForm.resetFields();
  };

  const handleEmailUpdate = (values: UpdateLoggedUserDTO) => {
    setConfirmLoading(true);
    updateUser({ email: values.email })
      .then((response) => {
        GLOB.userInfo = { ...GLOB.userInfo, ...response.data.data };
        closeEmailModal();
        Log.success('User email changed');
      }, (reason: AxiosError) => Log.error('Failed to change user email!', reason))
      .finally(() => setConfirmLoading(false));
  };

  return <div>
    <XmModal open={passModalOpen} destroyOnHidden={true} title="Change user password" confirmLoading={confirmLoading}
      onOk={() => passwordForm.submit()} onCancel={closePasswordModal}>
      <Form<UpdateLoggedUserDTO> form={passwordForm} preserve={false} id="changeUserPasswordForm" className="xm-form-validation" {...BasicFormProps}
        onFinish={handlePasswordUpdate}>
        <Form.Item
          name={path(t => t.oldPassword)}
          label="Current password"
          rules={[
            { required: true, whitespace: true, message: 'Existing password is required' }
          ]}
        >
          <Input.Password placeholder="Enter your current password" />
        </Form.Item>
        <Form.Item
          name={path(t => t.password)}
          label="New password"
          dependencies={[path(t => t.oldPassword)]}
          rules={[
            { required: true, whitespace: true, message: 'New password must be entered' },
            ({ getFieldValue }) => ({
              // Check if new password is different from old password
              validator: (_, value: string) => (!value || value !== (getFieldValue(path(t => t.oldPassword)) as string))
                ? Promise.resolve()
                : Promise.reject(new Error('New password must be different from current password'))
            }),
            ({ getFieldValue }) => ({
              // Check password strength; message can be a ReactNode for detailed formatting
              validator: (_, value: string) => (!value || PasswordUtil.isStrong(value, passwordRules))
                ? Promise.resolve()
                : Promise.reject(new Error()),
              message: PasswordUtilUI.getPasswordValidationMessage((getFieldValue(path(t => t.password)) as string) || "", passwordRules)
            })
          ]}
          hasFeedback
        >
          <XmInputPassword placeholder="Enter your new password" />
        </Form.Item>
        <Form.Item
          label="Confirm password"
          name="confirm"
          dependencies={[path(t => t.password)]}
          rules={[
            { required: true, whitespace: true, message: 'Repeat new password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue(path(t => t.password)) === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            })
          ]}
          hasFeedback
        >
          <XmInputPassword placeholder="Confirm your new password" />
        </Form.Item>
        <button type="submit" hidden />
      </Form>
    </XmModal>

    <XmModal open={emailModalOpen} destroyOnHidden={true} closable={false} title="Change user email" confirmLoading={confirmLoading}
      onOk={() => emailForm.submit()} onCancel={closeEmailModal} width={450}>
      <Form<UpdateLoggedUserDTO> form={emailForm} preserve={false} id="changeUserEmailForm" {...BasicFormProps} labelCol={{ span: 5 }} wrapperCol={null} initialValues={GLOB.userInfo}
        onFinish={handleEmailUpdate}>
        <Form.Item name={path(t => t.email)} label="Email" rules={[{ required: true, type: 'email', whitespace: true, message: 'Valid email required' }]}>
          <Input type="email" />
        </Form.Item>
      </Form>
    </XmModal>

    <Descriptions className="xm-profile" title="User Info" bordered column={1} style={{ maxWidth: '50em', paddingTop: '1em' }}>
      <Item label="Username">{GLOB.userInfo.username}</Item>
      {!GLOB.userInfo.ldap_id && <Item label="Password"><Button style={{ float: 'right' }} onClick={() => setPassModalOpen(true)}>Change...</Button>
      </Item>}
      <Item label="Email" style={{ lineHeight: 2 }}>{GLOB.userInfo.email}
        {!GLOB.userInfo.ldap_id && !sso_server && <Button style={{ float: 'right', marginLeft: '1em' }} onClick={() => setEmailModalOpen(true)}>Change...</Button>}
      </Item>
      <Item label="Admin">{String(GLOB.userInfo.isAdmin)}</Item>
      <Item label="Readonly">{String(GLOB.userInfo.readonly)}</Item>
      <Item label="Groups">{GLOB.userInfo.groups?.map(g => <Tag key={g.group_id} title={g.description}>{g.name}</Tag>)}</Item>
      {sso_server && <Item label="SSO server">{sso_server}</Item>}
      {GLOB.userInfo.ldapLabel && <Item label="LDAP cfg">{GLOB.userInfo.ldapLabel}</Item>}
      <Item label="Timezone">
        <XmTimeZoneSelect local={GLOB.userInfo.timezone} style={{ width: '20em' }}
          onChange={(value) => updateUserInfo({ timezone: value }, 'timezone')} />
      </Item>
      <Item label="Theme">
        <XmSelect
          ignoreReadonly
          value={theme.name}
          onChange={setTheme}
          style={{ width: '10em' }}
          options={[
            { value: 'blue', label: 'Blue' },
            { value: 'dark', label: 'Dark' },
            { value: 'light', label: 'Light' },
            { value: 'original', label: 'Original' },
          ]}
        />
      </Item>
      <Item label="Graph items limit">
        <div className="xm-profile-form-block">
          <div>
            <Checkbox checked={topItems != null}
              onChange={e => {
                const top = e.target.checked ? GLOB.userInfo.configuration?.top || GLOB.graphTopItems : null;
                setTopItems(top);
                changeTopItemsDebounced(top, historyItems, graphItemsText);
              }} />
            <Tooltip title={toolTipItemsText}>
              <span style={{ marginLeft: 8 }} title={`Current global top items: ${GLOB.graphTopItems}`}>Override global default</span>
            </Tooltip>
          </div>
          <Tooltip title={toolTipItemsText}>
            <InputNumber title={Label.GRAPH_TOP_ITEMS_TOOLTIP}
              disabled={topItems == null} min={0} step={10} value={topItems}
              onChange={value => {
                if (!!value) {
                  setTopItems(value);
                  changeTopItemsDebounced(value, historyItems, graphItemsText);
                }
              }} />
          </Tooltip>
        </div>
      </Item>
      <Item label="Graph sort order by">
        <XmSelect
          options={Object.values(TIMESERIES_TOP_ORDER).map(o => ({ label: o, value: o }))}
          defaultValue={GLOB?.userInfo?.configuration?.graphLegendOrderBy || TIMESERIES_TOP_ORDER.avg}
          onChange={(value) => updateUserInfo({ configuration: { graphLegendOrderBy: value } }, 'graph legend sort order by')} />
      </Item>
      <Item label="Show search history">
        <div className="xm-profile-form-block">
          <div>
            <Tooltip title={toolTipHistoryDisableText}>
              <Checkbox checked={historyItems != null} onChange={(checkedValue) => {
                const history = checkedValue.target.checked ? GLOB?.userInfo?.configuration?.searchHistoryLimit || 40 : null;
                setHistoryItems(history);
                changeTopItemsDebounced(topItems, history, searchHistoryText);
              }} />
            </Tooltip>
            <Tooltip title={toolTipHistoryText}>
              <span style={{ marginLeft: 8 }}>History length</span>
            </Tooltip>
          </div>
          <Tooltip title={toolTipHistoryText}>
            <InputNumber title={'histories'} value={historyItems} onChange={(value) => {
              if (value) {
                setHistoryItems(value);
                changeTopItemsDebounced(topItems, value, searchHistoryText);
              }
            }} disabled={!historyItems} min={1} step={1} />
          </Tooltip>
        </div>
      </Item>

      <Item label="Custom separators">
        <div>
          <Tooltip title={toolTipSeparatorDisableText}>
            <Checkbox checked={!!csvSeparator}
              onChange={checkedValue => {
                const csv = checkedValue.target.checked
                  ? SEPARATORS_CSV.SEMICOLON // GLOB.userInfo.configuration?.csvSeparator || globSettings.GLOB_CSV_SEPARATOR
                  : null;
                setCsvSeparator(csv);

                const decimal = checkedValue.target.checked
                  ? SEPARATORS_DECIMAL.DOT // GLOB.userInfo.configuration?.decimalSeparator || globSettings.GLOB_DECIMAL_SEPARATOR
                  : null;
                setDecimalSeparator(decimal);

                void updateUserInfo({ configuration: { decimalSeparator: decimal, csvSeparator: csv } }, 'decimal/CSV separator');
              }} >
              <span title={`Current decimal/CSV separator: ${globSettings.GLOB_DECIMAL_SEPARATOR} / ${globSettings.GLOB_CSV_SEPARATOR}`}>Override global default</span>
            </Checkbox>
          </Tooltip>
          <Row className="xm-separators" align="middle" justify="start" >
            <Col span={6} className="xm-separators-label"><label>Decimal:</label></Col>
            <Col span={6}>
              <XmSelect
                style={{ width: '4em' }}
                options={Object.values(SEPARATORS_DECIMAL).map(c => ({ label: c, value: c, disabled: c === csvSeparator, title: c === csvSeparator && 'Separators must differ' || null }))}
                value={decimalSeparator || globSettings.GLOB_DECIMAL_SEPARATOR}
                disabled={!decimalSeparator}
                placeholder={'.'}
                onSelect={(value) => {
                  setDecimalSeparator(value);
                  void updateUserInfo({ configuration: { decimalSeparator: value } }, 'decimal separator');
                }}
              />
            </Col>
            <Col span={6} className="xm-separators-label"><label>CSV:</label></Col>
            <Col span={6}>
              <XmSelect
                style={{ width: '4em' }}
                options={Object.values(SEPARATORS_CSV).map(c => ({ label: c, value: c, disabled: c === decimalSeparator, title: c === decimalSeparator && 'Separators must differ' || null }))}
                value={csvSeparator || globSettings.GLOB_CSV_SEPARATOR}
                disabled={!csvSeparator}
                placeholder={';'}
                onSelect={(value) => {
                  setCsvSeparator(value);
                  void updateUserInfo({ configuration: { csvSeparator: value } }, 'CSV separator');
                }}
              />
            </Col>
          </Row>
        </div>
      </Item>

      <Item label="Show release notes">
        <Switch checked={showReleaseNotes} onChange={changeShowReleaseNotes} />
      </Item>
    </Descriptions>
  </div>;
};

export default UserProfile;